/** SA-1060 (spec 2026-10-09 §2.1): the one card shell for a step, Seats,
 * Fields and Lanes on the process editors. Not `CollapsibleSection`: that
 * component has no id, no accessible name and no actions slot beside its
 * header (its whole header is one button), and SA-1059 is reshaping its
 * recipe in parallel. This copies the recipe's classes and migrates once
 * SA-1059 lands. Nothing is mounted behind a closed card. */
import { useId, type ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/utils'
import { cardAnchor, type CardId } from './editorCards'

function EditedMark() {
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-primary">
      <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-primary" />
      edited
    </span>
  )
}

export function ProcessEditorCard({
  cardId,
  title,
  label,
  summary,
  edited = false,
  open,
  onOpenChange,
  actions,
  children,
}: {
  cardId: CardId
  /** The visible heading ("11 Price and decide"). */
  title: string
  /** The section's accessible name ("Step 11: Price and decide"). */
  label: string
  /** The closed line; never blank. */
  summary: string
  edited?: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Rendered beside the toggle, outside it (document mode's move / remove). */
  actions?: ReactNode
  children: ReactNode
}) {
  const contentId = useId()
  return (
    <section
      id={cardAnchor(cardId)}
      aria-label={label}
      data-edited={edited ? 'true' : undefined}
      className="scroll-mt-24 rounded-lg border border-border bg-card shadow-xs"
    >
      <div className="flex items-start gap-2 px-4 py-3">
        <button
          type="button"
          onClick={() => onOpenChange(!open)}
          aria-expanded={open}
          aria-controls={open ? contentId : undefined}
          className="flex min-w-0 flex-1 items-start justify-between gap-3 text-left"
        >
          <span className="min-w-0">
            <span className="flex items-center gap-2">
              <span className="text-sm font-semibold text-foreground">{title}</span>
              {edited && <EditedMark />}
            </span>
            {!open && <span className="mt-0.5 block truncate text-xs text-muted-foreground">{summary}</span>}
          </span>
          <ChevronDown
            aria-hidden
            className={cn('mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')}
          />
        </button>
        {actions && <div className="shrink-0">{actions}</div>}
      </div>
      {open && (
        <div id={contentId} className="space-y-3 border-t border-border px-4 py-4">
          {children}
        </div>
      )}
    </section>
  )
}

/** A closed-by-default block inside an open step ("AI instruction and
 * threshold", "Contract and wording"): a named button, the body only while
 * open, remembered through the same `cards` the card itself uses. */
export function EditorDisclosure({
  title,
  open,
  onOpenChange,
  children,
}: {
  title: string
  open: boolean
  onOpenChange: (open: boolean) => void
  children: ReactNode
}) {
  const contentId = useId()
  return (
    <div className="text-xs">
      <button
        type="button"
        onClick={() => onOpenChange(!open)}
        aria-expanded={open}
        aria-controls={open ? contentId : undefined}
        className="inline-flex items-center gap-1 font-medium text-foreground"
      >
        <ChevronDown aria-hidden className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} />
        {title}
      </button>
      {open && (
        <div id={contentId} className="mt-2 space-y-3">
          {children}
        </div>
      )}
    </div>
  )
}
